import { BfPageStoreProvider, usePageStoreLifecycle, useStoreState } from '@tw/tw-runtime-react';
import { ProductGoodsDTO, ProductTypeEnumValue } from '@tw/clvip-api-gen';
import React, { useCallback, useState } from 'react';
import { Text, View } from 'react-native';
/*
 * Copyright (c) 2019 3will Corporation and others.
 * All rights reserved. This program and the accompanying materials
 * are made available under the terms of the 3will License v1.0
 * which accompanies this distribution, and is available at
 *
 *     http://www.3will.cn/licenses/license-v1.0
 *
 * Contributors:
 *    3will - initial API and implementation
 */
import {
  TwButton,
  TwFormItem,
  TwIcon,
  TwInputNumber,
  TwLoadingBlock,
  TwPage,
  TwRow,
  TwTypography
} from '@tw/tw-components-rn';

import { BfAppContext } from '@tw/tw-runtime';
import { Center } from 'native-base';
import { CommonStore } from '@/app/core/store/common/common-store';
import { GiftGiftInfoStore } from '@/gift/stores/gift-info/gift-gift-info-store';
import { GoodLimitTag } from '@/gift/component/good-limit-tag/GoodLimitTag';
import { InfoContainer } from '@/gift/pages/gift-info/viewparts/info-container/InfoContainer';
import { ShopTitle } from './viewparts/shop-title/ShopTitle';
import { TwColumn } from '@tw/tw-components-rn';
import { TwTcCommodityCardInfo } from '@tw/tw-templates-rn';
import { styles } from './styles';
import { ROUTE_NAMES } from '@/app/navigation/route-names';

const dayjs = require('dayjs');

export const GiftGiftInfo = (props) => {
  const [store] = useState(() => new GiftGiftInfoStore());
  const commonStore = CommonStore.getInstance();

  // Store生命周期
  usePageStoreLifecycle(store, props.route.params);
  const data = useStoreState(store, (state) => state.data);
  const editable = useStoreState(store, (state) => state.editable);
  const selectedGoods = useStoreState(commonStore, (state) => state.selectedGoods);
  const detailType = useStoreState(store, (state) => state.detailType);

  const getGoodCount = useCallback(
    (good: ProductGoodsDTO) => {
      const existingGoodIndex = selectedGoods?.findIndex((item) => item.good.id === good.id);
      if (existingGoodIndex >= 0) {
        console.log('getGoodCount', selectedGoods[existingGoodIndex].count);
        return selectedGoods[existingGoodIndex].count;
      } else {
        return 0;
      }
    },
    [selectedGoods]
  );

  if (!data) {
    return <TwLoadingBlock />;
  }

  return (
    <BfPageStoreProvider store={store}>
      <TwPage
        translucentStatusBar={true}
        style={styles.container}
        footer={
          detailType === 'product' ? null : (
            <View style={styles.commandContainer}>
              <TwButton
                style={styles.button}
                onPress={() => {
                  BfAppContext.getInstance().getRouteService().back();
                }}
                shape={'round'}
              >
                {editable ? '确认选择' : '确认'}
              </TwButton>
            </View>
          )
        }
      >
        <TwPage.Header title={'商品详情'}></TwPage.Header>
        <InfoContainer image={data.showPicture.url}>
          <View style={{ flex: 1 }}>
            {!data ? (
              <TwLoadingBlock />
            ) : (
              <>
                <View style={{ paddingHorizontal: 28 }}>
                  <View style={{ alignItems: 'center', padding: 12 }}>
                    <TwTypography.Text size="lg" style={{ marginBottom: 4 }}>
                      {data?.name}
                    </TwTypography.Text>
                    <TwTypography.Text size="sm" style={{ marginBottom: 4, color: '#565656' }}>
                      {data.desc}
                    </TwTypography.Text>
                  </View>

                  <TwRow alignItems={'center'} justifyContent={'space-between'} space={2}>
                    <Center flexDirection={'row'}>
                      {data.guidePrice > 0 && (
                        <TwRow alignItems={'center'}>
                          <Text style={styles.priceSymbol}>¥</Text>
                          <Text style={styles.priceNumber}>{data.guidePrice}</Text>
                        </TwRow>
                      )}
                      <Text style={styles.amount}>¥{data.price}</Text>
                      {data.chooseLimitMax && <GoodLimitTag chooseLimitMax={data.chooseLimitMax} />}
                    </Center>
                    {editable && (
                      <TwInputNumber
                        step={1}
                        precision={0}
                        value={getGoodCount(data)}
                        onChange={(num) => {
                          if (num > getGoodCount(data)) {
                            if (getGoodCount(data) >= data.chooseLimitMax) {
                              BfAppContext.getInstance().getMessageService().showWarn('已达到最大限购数量');
                            } else {
                              commonStore.addGoodToCart(data, 'order');
                            }
                          } else {
                            if (num < 0) {
                              return;
                            }
                            commonStore.deleteGoodToCart(data, 'order');
                          }
                        }}
                        min={0}
                        max={data.chooseLimitMax}
                        minusIcon={(isDisabled: boolean, color: string) => {
                          return <TwIcon icon="icon-minus-one" color={'#8FBD69'} size={24} />;
                        }}
                        plusIcon={(isDisabled: boolean, color: string) => {
                          return <TwIcon icon="icon-add-one" color={'#8FBD69'} size={24} />;
                        }}
                      />
                    )}
                  </TwRow>
                </View>

                <ShopTitle name={data?.supplier?.name} />
                {data.type?.code === ProductTypeEnumValue.fixed_package && (
                  <TwFormItem layout="vertical" label="礼包详情" labelStyle={styles.label}>
                    <TwColumn
                      space={2}
                      // style={styles.contentContainer}
                    >
                      {data.productInfo.map((item, index) => {
                        return (
                          <TwTcCommodityCardInfo
                            key={index}
                            title={item.productId.name}
                            subTitle={item?.desc ?? ''}
                            price={item.price}
                            num={item.num}
                            imageUrl={item.showPicture?.url}
                            onPress={() => {
                              BfAppContext.getInstance()
                                .getRouteService()
                                .push({
                                  path: ROUTE_NAMES.GiftGiftInfo,
                                  query: {
                                    id: item.productId.id,
                                    detailType: 'product'
                                  }
                                });
                            }}
                          />
                        );
                      })}
                    </TwColumn>
                  </TwFormItem>
                )}

                {/* <TwFormItem layout="vertical" label="详细说明" labelStyle={styles.label}>
                  <TwTypography.Text size="md" style={styles.dataItem}>
                    {data.desc}
                  </TwTypography.Text>
                </TwFormItem> */}
                <TwFormItem layout="vertical" label="有效日期" labelStyle={styles.label}>
                  <TwTypography.Text size="md" style={styles.dataItem}>
                    {dayjs(data.dualDate).format('YYYY-MM-DD HH:mm:ss')}
                  </TwTypography.Text>
                </TwFormItem>
                <TwFormItem layout="vertical" label="使用须知" labelStyle={styles.label}>
                  <TwTypography.Text size="md" style={styles.dataItem}>
                    {data.announcement}
                  </TwTypography.Text>
                </TwFormItem>

                {/* 礼包专区 */}
                {data.type?.code === 'fixed_package'}
                {/* 卡券专区 */}
                {data.type?.code === 'sale_card'}
              </>
            )}
          </View>
        </InfoContainer>
      </TwPage>
    </BfPageStoreProvider>
  );
};
